import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminTenant } from '@/features/tenants/useTenants';

import { classifyEditLoginError, useEditLoginPhone } from './useEditLoginPhone';
import { useTenantUsers, type TenantUser } from './useTenantUsers';

/**
 * SLICE 52 — change the phone number a tenant user SIGNS IN WITH.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE PREFILL SHOWS THE STORED VALUE EXACTLY, EVEN WHEN IT IS NOT E.164.
 * ─────────────────────────────────────────────────────────────────────────────
 * Jimi Test's owner was created with `611453000` — a number tenant-create never
 * normalised, so it cannot sign in (login normalises the typed number to
 * `+252611453000`, which is not what is stored). This dialog exists to fix exactly
 * that, so it must show the operator the WRONG value that is there, not a
 * cleaned-up guess: the field is prefilled with `user.phone` verbatim. The server
 * normalises what is submitted; this client never pre-normalises, so what gets
 * stored is the server's decision, visible on the next load.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE OPERATOR PICKS A PERSON, NOT A UUID — like the command-PIN dialog.
 * ─────────────────────────────────────────────────────────────────────────────
 * The picker fetches the tenant's users (`GET /api/admin/tenants/:tenantId/users`,
 * super-admin) and lists each by name (or phone when name is null), role, and the
 * number they currently sign in with. A DISABLED account is listed and labelled,
 * never hidden. The confirm names the PERSON and restates old → new before
 * anything is sent — this signs the user out of every session, so a single click
 * must not do it.
 */

const INPUT =
  'w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] outline-none focus:border-raad-primary';

export function EditLoginDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Super-admin only reaches this dialog (opened solely from the super-admin
  // action in `Tenants`), so the query is enabled unconditionally.
  const usersQ = useTenantUsers(tenant.id, true);
  const [userId, setUserId] = useState('');
  const [phone, setPhone] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ person: string; newPhone: string } | null>(null);
  const edit = useEditLoginPhone();

  const users = Array.isArray(usersQ.data?.users) ? usersQ.data.users : [];
  const selected = users.find((u) => u.id === userId) ?? null;
  const personName = (u: TenantUser) => u.name ?? u.phone;
  // A user is chosen and there is a number to submit. The server is the real
  // validator (it normalises and rejects), so this is a low floor, not a format
  // check the server also does.
  const ready = selected !== null && phone.trim().length >= 4;

  function pick(u: TenantUser) {
    setUserId(u.id);
    // 🔴 Prefill the STORED value verbatim — including a non-E.164 one. See header.
    setPhone(u.phone);
    setError(null);
  }

  async function submit() {
    if (!selected) return;
    setError(null);
    const typed = phone.trim();
    try {
      await edit.mutateAsync({ tenantId: tenant.id, userId: selected.id, phone: typed });
      setConfirming(false);
      setDone({ person: personName(selected), newPhone: typed });
    } catch (e) {
      setError(t(`editLogin.error.${classifyEditLoginError(e)}`));
      // Fall back to the form so the operator can correct the number.
      setConfirming(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('editLogin.title', { tenant: tenant.name })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-lg sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">
            {t('editLogin.title', { tenant: tenant.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('editLogin.close')}
          </button>
        </div>

        {done ? (
          <div>
            <p className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-[12.5px]">
              {t('editLogin.done', { person: done.person, tenant: tenant.name })}
            </p>
            {/* The two facts the operator must not miss: the user is signed out
                everywhere, and nothing will deliver the new number for them. */}
            <p className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12.5px]">
              {t('editLogin.signedOut')}
            </p>
            <p className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12.5px]">
              {t('editLogin.tellThem', { newPhone: done.newPhone })}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white"
              >
                {t('editLogin.doneButton')}
              </button>
            </div>
          </div>
        ) : confirming && selected ? (
          <div>
            <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12.5px]">
              {t('editLogin.confirmBody', {
                person: personName(selected),
                tenant: tenant.name,
                oldPhone: selected.phone,
                newPhone: phone.trim(),
              })}
            </p>
            {error ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px]"
              >
                {error}
              </p>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={edit.isPending}
                className="min-h-10 rounded-lg border border-raad-border px-4 text-[13px] font-semibold disabled:opacity-40"
              >
                {t('editLogin.back')}
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={edit.isPending}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {edit.isPending ? t('editLogin.saving') : t('editLogin.confirmButton')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mb-4 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12px] text-raad-muted">
              {t('editLogin.intro', { tenant: tenant.name })}
            </p>

            {usersQ.isPending ? (
              <p className="text-sm text-raad-muted" role="status">
                {t('editLogin.loading')}
              </p>
            ) : usersQ.isError ? (
              <p role="alert" className="text-sm text-raad-error">
                {t('editLogin.usersError')}
              </p>
            ) : users.length === 0 ? (
              <p className="text-sm text-raad-muted">{t('editLogin.noUsers')}</p>
            ) : (
              <fieldset className="grid gap-2">
                <legend className="mb-1 text-[12px] font-medium text-raad-muted">
                  {t('editLogin.pick')}
                </legend>
                {users.map((u) => {
                  const disabled = u.disabledAt !== null;
                  return (
                    <label
                      key={u.id}
                      className={[
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                        userId === u.id
                          ? 'border-raad-primary/60 bg-raad-primary/10'
                          : 'border-raad-border hover:bg-raad-primary/5',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        name="edit-login-user"
                        value={u.id}
                        checked={userId === u.id}
                        onChange={() => pick(u)}
                        className="mt-1"
                      />
                      <span className="block">
                        <span className="block text-[13.5px] font-medium">
                          {u.name ?? u.phone}
                          {disabled ? (
                            <span className="ms-2 inline-flex rounded-md border border-raad-muted/40 bg-raad-muted/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-muted">
                              {t('editLogin.disabled')}
                            </span>
                          ) : null}
                        </span>
                        <span className="mt-0.5 block text-[11.5px] text-raad-muted">
                          {t(`editLogin.role.${u.role}`)}
                          {' · '}
                          {t('editLogin.currentLogin')}{' '}
                          <span className="num">{u.phone}</span>
                        </span>
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            )}

            {selected ? (
              <div className="mt-4 block">
                <label className="block">
                  <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                    {t('editLogin.phone')}
                  </span>
                  <input
                    className={INPUT}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    inputMode="tel"
                    autoComplete="off"
                  />
                </label>
                <span className="mt-1 block text-[11.5px] text-raad-muted">
                  {t('editLogin.phoneHint')}
                </span>
              </div>
            ) : null}

            {error ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px]"
              >
                {error}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setConfirming(true);
                }}
                disabled={!ready}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {t('editLogin.review')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
